import type { ElementType, ReactNode } from 'react';
import { ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { useNavCollapsed } from '../../molecules/HFAppShell/navContext';

export interface HFNavItemProps {
  label: string;
  icon?: ReactNode;
  collapsed?: boolean;
  selected?: boolean;
  component?: ElementType;
  to?: string;
  onClick?: () => void;
}

export function HFNavItem({ label, icon, collapsed, selected = false, component, to, onClick }: HFNavItemProps) {
  const shellCollapsed = useNavCollapsed();
  const isCollapsed = collapsed ?? shellCollapsed;

  const sx = {
    minHeight: 56,
    borderRadius: 1.5,
    justifyContent: isCollapsed ? 'center' : 'flex-start',
    px: 1,
    py: 0,
    position: 'relative',
    color: 'text.secondary',
    '&:hover': {
      bgcolor: 'action.hover',
      color: 'text.primary',
    },
    '&.Mui-selected': {
      bgcolor: 'action.selected',
      color: 'primary.main',
      fontWeight: 600,
      '&:hover': {
        bgcolor: 'action.selected',
        color: 'primary.main',
      },
      '&::before': isCollapsed
        ? undefined
        : {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 10,
            bottom: 10,
            width: 3,
            borderRadius: 1,
            bgcolor: 'primary.main',
          },
    },
    '&.Mui-selected .MuiListItemIcon-root': {
      color: 'primary.main',
    },
  } as const;

  const content = (
    <>
      {icon ? (
        <ListItemIcon
          sx={{
            minWidth: isCollapsed ? 0 : 44,
            justifyContent: 'center',
            color: 'inherit',
            '& .MuiSvgIcon-root': { fontSize: 26 },
          }}
        >
          {icon}
        </ListItemIcon>
      ) : null}
      {!isCollapsed ? (
        <ListItemText
          primary={label}
          slotProps={{
            primary: {
              sx: { fontSize: '1rem', fontWeight: selected ? 600 : 500, letterSpacing: '-0.01em' },
              noWrap: true,
            },
          }}
        />
      ) : null}
    </>
  );

  const button =
    component != null ? (
      <ListItemButton component={component} to={to} selected={selected} onClick={onClick} aria-label={label} sx={sx}>
        {content}
      </ListItemButton>
    ) : (
      <ListItemButton selected={selected} onClick={onClick} aria-label={label} sx={sx}>
        {content}
      </ListItemButton>
    );

  if (isCollapsed && icon) {
    return (
      <Tooltip
        title={label}
        placement="right"
        slotProps={{
          tooltip: {
            sx: {
              fontSize: 14,
              fontWeight: 500,
              px: 1.5,
              py: 0,
              display: 'flex',
              alignItems: 'center',
              minHeight: 34,
            },
          },
        }}
      >
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
}
